import { useState, useRef, useEffect } from 'react';
import { Phone, Loader2, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { isValidIraqiPhone } from '@/lib/utils';

type Step = 'phone' | 'otp';

export default function LoginPage() {
  const { sendOtp, verifyOtp, loginWithGoogle, mapFirebaseError } = useAuth();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'otp') {
      setTimeout(() => otpInputRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSendOTP = async () => {
    setError('');
    const cleanPhone = phone.replace(/\s/g, '');

    if (!isValidIraqiPhone(cleanPhone)) {
      setError('رقم الهاتف غير صحيح');
      return;
    }

    setIsLoading(true);
    try {
      await sendOtp(cleanPhone);
      setCooldown(60);
      setStep('otp');
    } catch (err) {
      setError(mapFirebaseError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (code: string) => {
    if (code.length !== 6) return;
    setError('');
    setIsLoading(true);
    try {
      await verifyOtp(code, phone.replace(/\s/g, ''));
    } catch (err: any) {
      if (err?.code?.startsWith('auth/')) {
        setError(mapFirebaseError(err));
      } else {
        setError(err instanceof Error ? err.message : 'فشل التحقق');
      }
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    setOtp(digits);
    if (digits.length === 6) {
      handleVerifyOTP(digits);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/ramba-logo.png" alt="Ramba" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">Ramba Business</h1>
          <p className="text-gray-500 mt-1">رمبا للأعمال</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-card p-6">
          <div className="flex items-center gap-2 mb-6">
            <Phone className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-bold text-gray-900">تسجيل الدخول</h2>
          </div>

          {step === 'phone' ? (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف
                </label>
                <input
                  type="tel"
                  dir="ltr"
                  placeholder="07XX XXX XXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendOTP()}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-left placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm mb-4">{error}</p>
              )}

              <button
                onClick={handleSendOTP}
                disabled={isLoading || !phone}
                className="btn-primary w-full flex items-center justify-center gap-2 py-3"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'إرسال رمز التحقق'
                )}
              </button>
            </>
          ) : (
            <>
              {/* Back + phone display */}
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-sm text-primary-600 font-medium mb-4 hover:underline"
              >
                <ArrowRight className="w-4 h-4" />
                <span dir="ltr">{phone}</span>
                <span className="text-gray-400">— تغيير</span>
              </button>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رمز التحقق
                </label>
                <input
                  ref={otpInputRef}
                  type="text"
                  inputMode="numeric"
                  dir="ltr"
                  placeholder="------"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-center text-2xl tracking-[0.3em] font-bold placeholder:text-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm mb-4">{error}</p>
              )}

              {isLoading && (
                <div className="flex items-center justify-center gap-2 text-gray-500 mb-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">جاري التحقق...</span>
                </div>
              )}

              <button
                onClick={() => { setOtp(''); handleSendOTP(); }}
                disabled={isLoading || cooldown > 0}
                className={`w-full flex items-center justify-center gap-2 text-sm font-medium py-2 ${cooldown > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:underline'}`}
              >
                <RefreshCw className="w-4 h-4" />
                {cooldown > 0 ? `إعادة الإرسال بعد ${cooldown} ثانية` : 'إعادة إرسال الرمز'}
              </button>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">أو</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Google Sign-In */}
        <button
          onClick={async () => {
            setGoogleLoading(true);
            setError('');
            try {
              await loginWithGoogle();
            } catch (err: any) {
              if (err?.code === 'auth/popup-closed-by-user') return;
              setError(err instanceof Error ? err.message : 'فشل تسجيل الدخول عبر Google');
            } finally {
              setGoogleLoading(false);
            }
          }}
          disabled={googleLoading || isLoading}
          className="w-full flex items-center justify-center gap-3 bg-white rounded-2xl shadow-card p-4 hover:bg-gray-50 transition disabled:opacity-50"
        >
          {googleLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
          )}
          <span className="text-sm font-semibold text-gray-700">تسجيل الدخول عبر Google</span>
        </button>

        {error && step === 'phone' && (
          <p className="text-red-600 text-sm text-center mt-3">{error}</p>
        )}

        <p className="text-center text-gray-400 text-xs mt-6">
          تواصل مع الدعم للحصول على بيانات الدخول
        </p>
      </div>

      {/* Invisible reCAPTCHA container */}
      <div id="recaptcha-container" />
    </div>
  );
}
